import FavoriteIcon from '@mui/icons-material/Favorite';
import React, { useCallback, useEffect, useState } from 'react';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import { Link } from 'react-router-dom';
import { UserFavorite } from 'common/models/UserFavorite';
import { GetMyFavorites } from 'services/UsersService';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';

interface Props {
  isSideBarOpen?: boolean;
}

const entityNameMap: Record<number, string> = {
  1: 'Client',
  2: 'Contact',
  3: 'Candidate',
  4: 'Job',
  5: 'Placement',
  8: 'Opportunity',
  9: 'Site',
  15: 'Advert',
};

const getEntityPath = (entityId: number, recordId: number) => {
  switch (entityId) {
    case 1:
      return `/clients/${recordId}`;
    case 2:
      return `/contacts/${recordId}`;
    case 3:
      return `/candidates/${recordId}`;
    case 4:
      return `/jobs/${recordId}`;
    case 5:
      return `/placements/${recordId}`;
    case 8:
      return `/opportunities/${recordId}`;
    case 9:
      return `/sites/${recordId}`;
    case 15:
      return `/adverts/${recordId}`;
    case 21:
      return `/billing-groups/${recordId}`;
    default:
      return '';
  }
};

export function NavBarFavoritesMenu({ isSideBarOpen }: Props) {
  const [userFavorites, setUserFavorites] = useState<UserFavorite[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Optional: Add loading state
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [isRootHovered, setIsRootHovered] = useState(false);
  const [isRootPaperHovered, setIsRootPaperHovered] = useState(false);

  const handleOpenMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setIsRootHovered(true);
    setAnchor(e.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => setAnchor(null), []);

  useEffect(() => {
    if (Boolean(anchor) && !isRootHovered && !isRootPaperHovered) setAnchor(null);
  }, [anchor, isRootHovered, isRootPaperHovered]);

  useEffect(() => {
    async function fetchFavorites() {
      setIsLoading(true); // Start loading
      try {
        if (anchor) {
          const records = await GetMyFavorites();
          if (records) {
            setUserFavorites(records);
          } else {
            setUserFavorites([]); // Set to empty array to avoid errors
          }
        }
      } catch (error) {
        console.error('Error fetching favorites:', error);
        // Handle the error (e.g., display an error message)
      } finally {
        setIsLoading(false); // Stop loading, even if there's an error
      }
    }

    fetchFavorites();
  }, [anchor]); // Dependency array:  Run this effect only when 'anchor' changes

  return (
    <>
      <ListItem
        disablePadding
        sx={{ display: 'block', direction: 'ltr' }}
        onMouseEnter={handleOpenMenu}
        onMouseLeave={() => setIsRootHovered(false)}
      >
        <Tooltip
          title="Favorites"
          placement="right"
          disableHoverListener={isSideBarOpen}
          slotProps={{
            popper: { modifiers: [{ name: 'offset', options: { offset: [-35, 0] } }] },
          }}
        >
          <ListItemButton
            sx={{ paddingY: 0, px: 2.5, justifyContent: isSideBarOpen ? 'initial' : 'center' }}
            onClick={handleOpenMenu}
          >
            <ListItemIcon
              sx={{ minWidth: 0, justifyContent: 'center', mr: isSideBarOpen ? 3 : 'auto' }}
            >
              <FavoriteIcon
                sx={{ color: (t) => t.palette.getContrastText(t.palette.primary.main) }}
              />
            </ListItemIcon>
            <ListItemText primary="Favorites" sx={{ opacity: isSideBarOpen ? 1 : 0 }} />
          </ListItemButton>
        </Tooltip>
      </ListItem>
      <Popper
        anchorEl={anchor}
        open={Boolean(anchor)}
        placement="right-start"
        sx={{ zIndex: (t) => t.zIndex.appBar - 1 }}
        onMouseEnter={() => setIsRootPaperHovered(true)}
        onMouseLeave={() => setIsRootPaperHovered(false)}
      >
        <ClickAwayListener onClickAway={handleCloseMenu}>
          <Paper
            elevation={8}
            onMouseLeave={handleCloseMenu}
            sx={{ maxHeight: '100vh', overflow: 'auto' }}
          >
            <MenuList>
              {isLoading && userFavorites.length === 0 && <MenuItem>Loading...</MenuItem>}
              {!isLoading && userFavorites.length === 0 && <MenuItem>No Favorites</MenuItem>}
              {userFavorites.length > 0 &&
                userFavorites.map((r) => (
                  <Link
                    key={`${r.entityId}-${r.recordId}`}
                    to={getEntityPath(r.entityId, r.recordId)}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    <MenuItem>
                      <ListItemText slotProps={{ primary: { fontSize: '0.885rem' } }}>
                        {r.recordName}
                      </ListItemText>
                      <Chip
                        size="small"
                        label={entityNameMap[r.entityId] ?? '(UNKNOWN)'}
                        sx={{ ml: 2, borderRadius: '6px' }}
                      />
                    </MenuItem>
                  </Link>
                ))}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
